import { Paper, Text, Flex, Button } from '@mantine/core';
import { IconChevronRight, IconFileText, IconFileUpload } from '@tabler/icons-react';
import { Link } from 'react-router';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { assignSpeakerPath } from 'src/meetings/scope';
import { speakerReviewPrompt } from 'src/meetings/utils/speaker-utils';
import type { CalendarEvent } from 'src/meetings/models';
import {
  formatClientLabel,
  getClientDisplayNames,
  getFirstClientParticipant,
} from 'src/meetings/utils/event-utils';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import { EventTime } from 'src/meetings/components/EventTime';

type Props = {
  event: CalendarEvent;
};

export const PastEventCard = ({ event }: Props) => {
  const { flagEnabledShowDesktopRecordings, flagEnabledMeetingsRework } = useFlags();
  const clientFamilyMap = useClientFamilyMap();
  // On the practice-wide hub, collapse each family's attendees to a single
  // "<name> family" label rather than listing one member — the upcoming card beside
  // this one does the same, so the two lists must not disagree about a meeting.
  const clientName =
    formatClientLabel(getClientDisplayNames(event, clientFamilyMap)) ||
    (flagEnabledShowDesktopRecordings ? 'No clients identified' : 'Unknown client');

  return (
    <Paper variant="card" p="md">
      <Flex justify="space-between" align="center">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
          {clientName}
        </Text>
        {!flagEnabledShowDesktopRecordings || event.meetingPlatform || !event.hasTranscription ? (
          <EventTime
            scheduledStartAt={event.scheduledStartAt}
            scheduledEndAt={event.scheduledEndAt}
            startAt={event.startAt}
            endAt={event.endAt}
            platform={event.meetingPlatform}
          />
        ) : (
          <Flex align="center" gap="xxs" c="gray.6">
            <IconFileUpload size={16} />
            <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
              Added manually
            </Text>
          </Flex>
        )}
      </Flex>

      <Link
        to={`/meetings/${event.id}/summary`}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10" mt="xxxs">
          {event.title}
        </Text>

        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" mt="xxs" lineClamp={2}>
          {event.summary}
        </Text>
      </Link>

      {flagEnabledMeetingsRework && event.hasTranscription && <SpeakerReviewHint event={event} />}

      <Flex
        justify="space-between"
        align="center"
        mt="sm"
        pt="sm"
        style={{ borderTop: '1px solid var(--mantine-color-gray-1)' }}
      >
        <MeetingNoteButton event={event} />
        <Button
          component={Link}
          style={{ overflow: 'visible' }}
          to={`/meetings/${event.id}/summary`}
          variant="text-action"
          rightSection={<IconChevronRight size={16} />}
        >
          More actions
        </Button>
      </Flex>
    </Paper>
  );
};

function SpeakerReviewHint({ event }: { event: CalendarEvent }) {
  const prompt = speakerReviewPrompt(event);

  if (event.transcriptionStatus !== TranscriptionStatus.READY || !prompt) return null;

  return (
    <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" mt="xs">
      {prompt.count} speaker{prompt.count === 1 ? '' : 's'} {prompt.state}.{' '}
      <Text
        span
        inherit
        c="brand.7"
        component={Link}
        to={assignSpeakerPath({ kind: 'all' }, event.id, prompt.speakerLabel)}
        style={{ textDecoration: 'none' }}
      >
        {prompt.action}
      </Text>{' '}
      {prompt.reason}
    </Text>
  );
}

function MeetingNoteButton({ event }: { event: CalendarEvent }) {
  const { flagEnabledShowDesktopRecordings } = useFlags();
  if (
    flagEnabledShowDesktopRecordings &&
    event.hasTranscription &&
    !getFirstClientParticipant(event)
  ) {
    return (
      <Button
        component={Link}
        style={{ overflow: 'visible' }}
        to={`/meetings/${event.id}/summary`}
        variant="text-action"
      >
        Assign clients to generate meeting note
      </Button>
    );
  }

  if (!event.hasMeetingNote) {
    return (
      <Button
        component={Link}
        style={{ overflow: 'visible' }}
        to={`/meetings/${event.id}/meeting-note`}
        variant="text-action"
      >
        Generate meeting note
      </Button>
    );
  }

  return (
    <Button
      component={Link}
      style={{
        overflow: 'visible',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textDecorationThickness: '15%',
        textUnderlineOffset: '17%',
      }}
      to={`/meetings/${event.id}/meeting-note`}
      variant="text-action"
    >
      <IconFileText size={16} style={{ marginRight: 'var(--mantine-spacing-xxxs)' }} />
      Meeting note
    </Button>
  );
}
